import { LineItemsGrid } from '@iheartjane/menu';
import { Typography } from '@iheartjane/reefer';

import { useCartState } from '@jane/monolith/components/cart/cartFacade/useCartState';
import { selectCart } from '@jane/monolith/customer/redux/cart';
import { useCustomerSelector } from '@jane/monolith/customer/selectors';
import { reservationModeLabel } from '@jane/monolith/utils/store';
import { useGetStore } from '@jane/shared/data-access';
import type { CartLineItems } from '@jane/shared/types';

import { SummaryWrapper } from './cartDrawerSummary.styles';

interface CartDrawerSummaryProps {
  cartLineItems: CartLineItems;
  isChangingReservationModes: boolean;
}

const MaxSpecialsDisclaimer = ({
  maxSpecialsPerCart,
}: {
  maxSpecialsPerCart: number | null;
}) => {
  if (maxSpecialsPerCart) {
    const pluralizedLabel = maxSpecialsPerCart > 1 ? 'specials' : 'special';
    return (
      <Typography color="grays-mid" as="p" my={8} variant="small-body-regular">
        This store supports {maxSpecialsPerCart + ' ' + pluralizedLabel + ' '}
        per order. Don't worry, we've already selected the {
          pluralizedLabel
        }{' '}
        that saves you the most.
      </Typography>
    );
  }
  return null;
};

export const CartDrawerSummary = ({
  cartLineItems,
  isChangingReservationModes,
}: CartDrawerSummaryProps) => {
  const { deliveryAddress } = useCustomerSelector(selectCart);
  const { cart } = useCartState();
  const {
    preDiscountSubtotal,
    deliveryFee,
    discountTotal,
    feeTax,
    roundingAmount,
    serviceFee,
    storeTax,
    salesTax,
    tipAmount,
  } = cartLineItems;

  const {
    reservation_mode: reservationMode,
    max_specials_per_cart: maxSpecialsPerCart,
    max_specials_per_cart_hit: maxSpecialsPerCartHit,
  } = cart ?? {};

  const { data: store } = useGetStore(cart?.store?.id);

  const hasJanePayServiceFee = Boolean(
    store?.jane_pay_integration?.consumer_fee_per_transaction ||
    store?.jane_pay_integration?.consumer_fee_rate ||
    (cart?.jane_pay_service_fee_amount ?? 0) > 0
  );

  return (
    <SummaryWrapper flexDirection="column">
      <LineItemsGrid
        color="grays-dark"
        deliveryFee={deliveryFee || 0}
        deliveryLabel={reservationModeLabel(store, 'delivery')}
        discountTotal={discountTotal}
        feeTax={feeTax}
        hasDeliveryAddress={Boolean(deliveryAddress)}
        hasJanePayServiceFee={hasJanePayServiceFee}
        isRecalculating={isChangingReservationModes}
        janePayEnabled={Boolean(store?.jane_pay_integration?.enabled)}
        preDiscountSubtotal={preDiscountSubtotal}
        reservationMode={reservationMode!}
        reservationModeLabel="Delivery"
        roundingAmount={roundingAmount}
        salesTax={salesTax}
        serviceFee={serviceFee}
        showTip={tipAmount > 0}
        storeTax={storeTax}
        taxIncluded={store?.tax_included}
        tipAmount={tipAmount}
      />

      {maxSpecialsPerCartHit && (
        <MaxSpecialsDisclaimer maxSpecialsPerCart={maxSpecialsPerCart!} />
      )}
    </SummaryWrapper>
  );
};
